/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import {useMachine} from '@xstate/react';
import {faIdCard} from '@fortawesome/free-solid-svg-icons';

import {
  Alert,
  Button,
  ContentBody,
  FieldWrapper,
  NxLoadWrapper,
  NxSubmitMask,
  Page,
  PageHeader,
  PageTitle,
  Section,
  SectionFooter,
  Select,
  Textarea,
  Textfield,
  Utils
} from 'nexus-ui-plugin';

import SamlConfigurationMachine from './SamlConfigurationMachine';
import UIStrings from '../../../../constants/UIStrings';

import './SamlConfiguration.scss';

const FIELDS = UIStrings.SAML_CONFIGURATION.FIELDS;

export default function SamlConfiguration() {
  const [current, send, service] = useMachine(SamlConfigurationMachine, {devTools: true});

  const {data : settings, isPristine, validationErrors, saveError, loadError} = current.context;
  const isLoading = current.matches('loading');
  const isSaving = current.matches('saving');
  const isInvalid = Utils.isInvalid(validationErrors);
  const errorMessage = saveError;
  const hasData = settings && settings !== {};

  function updateSettings({target}) {
    send('UPDATE', {
      data: {
        [target.name]: target.value
      }
    });
  }

  function saveSettings(event) {
    event.preventDefault();
    send('SAVE');
  }

  function discardChanges() {
    send('RESET');
  }

  function handleEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      send('SAVE');
    }
  }

  let error = null;
  if (errorMessage instanceof Array) {
    error = <Alert type="error">
      {UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_ERROR}
      <ul>
        {errorMessage.map(error => <li key={error.id}>{JSON.stringify(error)}</li>)}
      </ul>
    </Alert>;
  }
  else if (errorMessage) {
    error = <Alert type="error">
      {UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_ERROR}<br/>
      {errorMessage}
    </Alert>;
  }

  return <Page>
    <PageHeader><PageTitle icon={faIdCard} {...UIStrings.SAML_CONFIGURATION.MENU}/></PageHeader>
    <ContentBody className='nxrm-saml-configuration'>
      <Section>
      <NxLoadWrapper loading={isLoading} error={loadError ? `${loadError}` : null}>
        {hasData && <>
          {isSaving && <NxSubmitMask message={UIStrings.SAVING}/>}
          {error}

          <FieldWrapper labelText={FIELDS.idpMetadataLabel}>
            <Textarea validationErrors={validationErrors['idpMetadata']} name="idpMetadata" value={settings.idpMetadata} onChange={updateSettings}/>
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.entityIdUriLabel} descriptionText={FIELDS.entityIdUriDescription}>
            <Textfield
                name="entityIdUri"
                value={settings.entityIdUri}
                onChange={updateSettings}
                onKeyPress={handleEnter}
                validationErrors={validationErrors['entityIdUri']}
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.validateResponseSignatureLabel} descriptionText={FIELDS.validateResponseSignatureDescription}>
            <Select name="validateResponseSignature" value={settings.validateResponseSignature} onChange={updateSettings}>
              <option value="default">Default</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.validateAssertionSignatureLabel} descriptionText={FIELDS.validateAssertionSignatureDescription}>
            <Select name="validateAssertionSignature" value={settings.validateAssertionSignature} onChange={updateSettings}>
              <option value="default">Default</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          </FieldWrapper>

          <fieldset>
            <legend>IdP Field Mappings</legend>

            <FieldWrapper labelText={FIELDS.usernameAttrLabel}>
              <Textfield
                  name="usernameAttr"
                  value={settings.usernameAttr}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
                  validationErrors={validationErrors['usernameAttr']}
              />
            </FieldWrapper>

            <FieldWrapper labelText={FIELDS.firstNameAttrLabel}>
              <Textfield
                  name="firstNameAttr"
                  value={settings.firstNameAttr}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>

            <FieldWrapper labelText={FIELDS.lastNameAttrLabel}>
              <Textfield
                  name="lastNameAttr"
                  value={settings.lastNameAttr}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>

            <FieldWrapper labelText={FIELDS.emailAttrLabel}>
              <Textfield
                  name="emailAttr"
                  value={settings.emailAttr}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>

            <FieldWrapper fieldName="roleAttr" labelText={FIELDS.roleAttrLabel}>
              <Textfield
                  name="roleAttr"
                  value={settings.roleAttr}
                  onChange={updateSettings}
                  onKeyPress={handleEnter}
              />
            </FieldWrapper>
          </fieldset>

          <SectionFooter>
            <Button variant="primary" disabled={isPristine || isInvalid} onClick={saveSettings} type="submit">
              {UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
            </Button>
            <Button disabled={isPristine} onClick={discardChanges}>
              {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
            </Button>
          </SectionFooter>
        </>}
        </NxLoadWrapper>
      </Section>
    </ContentBody>
  </Page>;
}
